import * as i from 'types';
import * as React from 'react';
import { GetStaticPaths } from 'next';
import Head from 'next/head';

import { useRouter } from 'hooks';
import { isServer } from 'services';
import { getMainNavigation, getFooter } from 'services/contentful';
import contentfulClient from 'services/contentful/contentfulClient';
import PageLayout from 'layouts/PageLayout';
import { PageBody, PageHeader, PageNavigation } from 'modules/page';

const PageGeneral: i.NextPageComponent<PageGeneralProps> = ({ page }) => {
  const router = useRouter();

  React.useEffect(() => {
    const authenticationCookieValue =
      !isServer &&
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('authenticated='))
        ?.split('=')[1];
    const isAuthenticated = Boolean(authenticationCookieValue);

    if (!isAuthenticated && !isServer) {
      router.push('/login');
    }
  }, []);

  return (
    <>
      <Head>
        <title>Goed Gezien - {page?.fields.title}</title>
        <meta name="description" content={page?.fields.seo?.fields.metaDescription} />
      </Head>
      {page.fields.header && <PageHeader data={page.fields} />}
      <PageNavigation fields={page.fields} />
      <PageBody fields={page.fields} />
    </>
  );
};

type PageGeneralProps = {
  page: i.TypePageGeneral;
};

export const getStaticPaths: GetStaticPaths<{ pageGeneral: string }> = async () => {
  const pages = await contentfulClient.withoutUnresolvableLinks.getEntries<i.TypePageGeneralFields>(
    {
      content_type: 'pageGeneral',
    },
  );
  const paths = pages.items.map((item) => ({
    params: { pageGeneral: item.fields.seo!.fields.slug },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = async ({ params }) => {
  const page = await contentfulClient.withoutUnresolvableLinks.getEntries<i.TypePageGeneralFields>({
    content_type: 'pageGeneral',
    'fields.seo.sys.contentType.sys.id': 'seo',
    'fields.seo.fields.slug': params.pageGeneral,
    include: 10,
  });
  const navigation = await getMainNavigation();
  const footer = await getFooter('mainFooter');

  return {
    props: {
      page: page.items[0],
      navigation,
      footer,
    },
  };
};

PageGeneral.layout = (page, pageProps) => {
  return (
    <PageLayout
      navigation={pageProps.navigation}
      footer={pageProps.footer}
      withoutContainer
      withoutPadding
    >
      {page}
    </PageLayout>
  );
};

export default PageGeneral;
